import React, { useEffect, useMemo } from 'react'
import { createContext, Dispatch, useReducer } from 'react'
import {
  TaskPublic,
  CreateTaskRequestDiscriminator,
  TaskStatePublicDiscriminator,
} from '@chilipiper/api-client-kosmotime'
import { useCurrentSession } from '@/api/kosmotime'

export type KosmotimeTabOption = 'Today' | 'Upcoming' | 'Unscheduled' | 'All tasks'

interface KosmotimeState {
  isCollapsed: boolean
  isFullScreen: boolean
  currentTab: KosmotimeTabOption
  currentSessionTask?: TaskPublic
  scheduledTaskDate?: string
  createTaskType?: CreateTaskRequestDiscriminator
  updateTaskType?: Omit<TaskStatePublicDiscriminator, 'Completed'>
  isCreatingOrUpdatingUnscheduledTask: boolean
}

const initialState: KosmotimeState = {
  isCollapsed: true,
  isFullScreen: false,
  isCreatingOrUpdatingUnscheduledTask: false,
  currentTab: 'Today',
}

interface SetIsCollapsedAction {
  payload: boolean
  type: 'SET_IS_COLLAPSED'
}

interface SetIsFullScreenAction {
  payload: boolean
  type: 'SET_IS_FULL_SCREEN'
}

interface SetUpdateTaskType {
  payload: Omit<TaskStatePublicDiscriminator, 'Completed'> | undefined
  type: 'SET_UPDATE_TASK_TYPE'
}

interface SetCreateTaskType {
  payload: CreateTaskRequestDiscriminator | undefined
  type: 'SET_CREATE_TASK_TYPE'
}

interface SetCurrentTabAction {
  payload: KosmotimeTabOption
  type: 'SET_CURRENT_TAB'
}

interface SetScheduledTaskDateAction {
  payload: string | undefined
  type: 'SET_SCHEDULED_TASK_DATE'
}

interface SetCurrentSessionTaskAction {
  payload: TaskPublic | undefined
  type: 'SET_CURRENT_SESSION_TASK'
}

interface SetCreateTaskTypeAndDateAction {
  type: 'SET_CREATE_TASK_TYPE_AND_DATE'
  payload: {
    createTaskType: CreateTaskRequestDiscriminator
    scheduledTaskDate: string
  }
}

interface SetUpdateTaskTypeAndDateAction {
  type: 'SET_UPDATE_TASK_TYPE_AND_DATE'
  payload: {
    updateTaskType: Omit<TaskStatePublicDiscriminator, 'Completed'>
    scheduledTaskDate: string
  }
}

interface SetCreateAndUpdateTaskType {
  type: 'SET_CREATE_AND_UPDATE_TASK_TYPE'
  payload: {
    updateTaskType: Omit<TaskStatePublicDiscriminator, 'Completed'> | undefined
    createTaskType: CreateTaskRequestDiscriminator | undefined
  }
}

type KosmotimeStateAction =
  | SetIsCollapsedAction
  | SetCurrentTabAction
  | SetCurrentSessionTaskAction
  | SetIsFullScreenAction
  | SetCreateTaskType
  | SetScheduledTaskDateAction
  | SetCreateTaskTypeAndDateAction
  | SetUpdateTaskType
  | SetUpdateTaskTypeAndDateAction
  | SetCreateAndUpdateTaskType

const kosmotimeStateReducer = (state: KosmotimeState, action: KosmotimeStateAction) => {
  const { type, payload } = action

  switch (type) {
    case 'SET_CURRENT_TAB':
      return { ...state, currentTab: payload }
    case 'SET_SCHEDULED_TASK_DATE':
      return { ...state, scheduledTaskDate: payload }
    case 'SET_IS_COLLAPSED':
      return { ...state, isCollapsed: payload }
    case 'SET_IS_FULL_SCREEN':
      return { ...state, isFullScreen: payload }
    case 'SET_CURRENT_SESSION_TASK':
      return { ...state, currentSessionTask: payload }
    case 'SET_CREATE_TASK_TYPE':
      return { ...state, createTaskType: payload }
    case 'SET_UPDATE_TASK_TYPE':
      return { ...state, updateTaskType: payload }
    case 'SET_CREATE_TASK_TYPE_AND_DATE':
      return {
        ...state,
        createTaskType: payload.createTaskType,
        scheduledTaskDate: payload.scheduledTaskDate,
      }
    case 'SET_UPDATE_TASK_TYPE_AND_DATE':
      return {
        ...state,
        updateTaskType: payload.updateTaskType,
        scheduledTaskDate: payload.scheduledTaskDate,
      }
    case 'SET_CREATE_AND_UPDATE_TASK_TYPE':
      return {
        ...state,
        updateTaskType: payload.updateTaskType,
        createTaskType: payload.createTaskType,
      }
  }
}

interface KosmotimeStateContextType {
  dispatch: Dispatch<KosmotimeStateAction>
  state: KosmotimeState
}

export const KosmotimeStateContext = createContext<KosmotimeStateContextType>(
  {} as KosmotimeStateContextType
)

export const KosmotimeStateProvider = ({ children }) => {
  const [state, dispatch] = useReducer(kosmotimeStateReducer, initialState)

  const { data: currentSession } = useCurrentSession()

  useEffect(() => {
    if (currentSession?.type === 'NotRunning') {
      dispatch({ type: 'SET_CURRENT_SESSION_TASK', payload: undefined })
    }
  }, [currentSession])

  const value = useMemo(() => ({ state, dispatch }), [state, dispatch])

  return <KosmotimeStateContext value={value}>{children}</KosmotimeStateContext>
}
